import classNames from 'classnames'
import type { RefObject } from 'react'
import { useId } from 'react'
import { useCallback, useEffect, useRef } from 'react'
import { ControlFieldAlign } from '../../../types'
import { useControlAriaAttributes } from '../hooks'
import styles from './Checkbox.module.scss'
import { CheckboxState } from './types'
import type { CheckboxProp, CheckboxStateType } from './types'

const getNextState = (state: CheckboxStateType | undefined, isChecked: boolean) => {
  if (state !== undefined) {
    return state === CheckboxState.unchecked || state === CheckboxState.indeterminate
      ? CheckboxState.checked
      : CheckboxState.unchecked
  } else {
    return !isChecked
  }
}

const Checkbox: React.FC<CheckboxProp> = ({
  align = ControlFieldAlign.left,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  checked,
  className,
  disabled,
  error = false,
  label,
  onChange,
  state,
}: CheckboxProp) => {
  const checkboxRef: RefObject<HTMLInputElement> = useRef<HTMLInputElement>(null)
  const isChecked = state === CheckboxState.checked || !!(state === undefined && checked)
  const computedState = state || (isChecked ? CheckboxState.checked : CheckboxState.unchecked)
  const ariaChecked = state === CheckboxState.indeterminate ? 'mixed' : isChecked
  const uniqueId = useId()
  useEffect(() => {
    if (!checkboxRef.current) return
    checkboxRef.current.indeterminate = state === CheckboxState.indeterminate
  }, [checkboxRef, state])

  const handleChange = useCallback(() => {
    onChange && onChange(getNextState(state, isChecked))
  }, [isChecked, onChange, state, getNextState])

  const { ariaAttributes } = useControlAriaAttributes({ ariaLabel, ariaLabelledBy, label, labelId: uniqueId })
  return (
    <div className={classNames(styles['pintxo-checkbox'], className)} data-align={align}>
      <input
        aria-checked={ariaChecked}
        type="checkbox"
        disabled={disabled}
        checked={isChecked}
        ref={checkboxRef}
        onChange={handleChange}
        data-state={computedState}
        data-checked={isChecked}
        data-disabled={disabled}
        data-error={error}
        className={styles['pintxo-checkbox__input']}
        {...ariaAttributes}
      />
      {label && (
        <span className={styles['pintxo-checkbox__label']} data-disabled={disabled} id={uniqueId}>
          {label}
        </span>
      )}
    </div>
  )
}
export default Checkbox
